import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, FileCheck2, Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { BRAND } from "@/constants/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import VendorApplyForm, { emptyApplication, toPayload, checkApplication } from "@/pages/vendor/VendorApplyForm";

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { register } = useAuth();
  const isVendor = params.get("role") === "vendor";
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [biz, setBiz] = useState(emptyApplication());
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (isVendor) {
      const problem = checkApplication(biz);
      if (problem) return toast.error(problem);
      if (!agree) return toast.error("Please accept the seller agreement to continue.");
    }
    setLoading(true);
    try {
      const payload = { ...form, role: isVendor ? "vendor" : "customer" };
      if (isVendor) payload.vendor = toPayload(biz);
      const user = await register(payload);
      toast.success(isVendor ? "Application submitted! We'll review it shortly." : "Account created successfully!");
      navigate(user.role === "vendor" ? "/vendor" : "/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-[#04301b] via-[#0a4a27] to-[#1b6b24] text-white p-12 lg:sticky lg:top-0 lg:h-screen">
        <Link to="/"><img src={BRAND.logo} alt="Zilkart" className="h-12 bg-white rounded-xl px-3 py-1.5 shadow-lg" /></Link>
        <div>
          <h2 className="font-display text-4xl font-bold leading-tight">
            {isVendor ? "Start selling on Zilkart" : "Join Zilkart today"}
          </h2>
          <p className="mt-4 text-white/80 max-w-sm">
            {isVendor ? "Reach customers across India and grow your business with us." : "Create an account to shop, save favourites and checkout faster."}
          </p>
          {isVendor && (
            <ul className="mt-8 space-y-4 text-sm text-white/85 max-w-sm">
              <li className="flex gap-3"><FileCheck2 className="h-5 w-5 text-[#e9c46a] shrink-0" /> Apply in minutes — our team reviews every seller before going live.</li>
              <li className="flex gap-3"><ShieldCheck className="h-5 w-5 text-[#e9c46a] shrink-0" /> Every product is quality-checked to keep customers' trust.</li>
              <li className="flex gap-3"><Wallet className="h-5 w-5 text-[#e9c46a] shrink-0" /> Transparent commission and payouts straight to your bank account.</li>
            </ul>
          )}
        </div>
        <p className="text-sm text-white/60">© {new Date().getFullYear()} {BRAND.name} Technologies</p>
      </div>

      <div className={`flex justify-center p-6 sm:p-12 ${isVendor ? "items-start" : "items-center"}`}>
        <div className={`w-full ${isVendor ? "max-w-2xl" : "max-w-sm"}`}>
          <Link to="/" className="lg:hidden inline-block mb-8"><img src={BRAND.logo} alt="Zilkart" className="h-9" /></Link>
          <h1 className="font-display text-3xl font-bold text-deep">{isVendor ? "Apply to become a seller" : "Create Account"}</h1>
          <p className="text-muted-foreground text-sm mt-1 mb-6">
            {isVendor ? "Create your login and tell us about your business. We'll notify you once it's reviewed." : "It only takes a minute."}
          </p>
          <form onSubmit={submit} className="space-y-4">
            {isVendor && <h3 className="font-display text-lg font-bold text-deep border-b border-border pb-2">Your account</h3>}
            <div className={isVendor ? "grid sm:grid-cols-2 gap-4" : "space-y-4"}>
              <div>
                <Label>Full Name</Label>
                <Input value={form.name} onChange={set("name")} required data-testid="register-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={set("email")} required data-testid="register-email" />
              </div>
              {!isVendor && (
                <div>
                  <Label>Phone (optional)</Label>
                  <Input value={form.phone} onChange={set("phone")} data-testid="register-phone" />
                </div>
              )}
              <div>
                <Label>Password</Label>
                <Input type="password" value={form.password} onChange={set("password")} required data-testid="register-password" />
              </div>
            </div>

            {isVendor && (
              <div className="pt-4 space-y-6">
                <VendorApplyForm form={biz} setForm={setBiz} />
                <label className="flex items-start gap-2.5 text-sm text-muted-foreground cursor-pointer">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="va-agree" />
                  <span>I confirm the details above are correct and I agree to the Zilkart <Link to="/terms" className="text-primary font-semibold hover:underline" target="_blank">seller terms</Link>, including commission and payout policies.</span>
                </label>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
              {loading ? "Submitting…" : isVendor ? "Submit application" : "Create Account"}
            </Button>
          </form>
          <p className="text-sm text-center mt-6 text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="register-to-login">Sign in</Link>
          </p>
          {!isVendor && (
            <p className="text-sm text-center mt-2 text-muted-foreground">
              Want to sell?{" "}
              <Link to="/register?role=vendor" className="text-primary font-semibold hover:underline">Become a vendor</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
